import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setError("This invite link is missing a token.");
      return;
    }
    try {
      const { data } = await api.get(`/auth/invite/${token}`);
      setInfo(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/accept-invite", { token, new_password: pw });
      toast.success("Welcome to Audentra");
      await checkAuth();
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error("Could not accept invite", { description: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-start justify-start px-6 md:px-16 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <div className="h-9 w-9 rounded-md bg-teal-700 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold tracking-tight">Audentra</div>
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-slate-500">Compliance Hub</div>
          </div>
        </Link>

        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900" data-testid="invite-error">
            <div className="font-semibold">This invite can&apos;t be used</div>
            <div className="mt-1">{error}</div>
            <Link to="/login" className="mt-4 inline-flex items-center text-teal-700 font-semibold text-sm">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
            </Link>
          </div>
        )}

        {!error && !info && (
          <div className="text-slate-500">Loading invite…</div>
        )}

        {info && (
          <>
            <div className="label-caps text-teal-700 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Invite valid
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
              Welcome to <span style={{ color: info.brand_primary_color }}>{info.org_name}</span>
            </h1>
            <p className="text-slate-600 mt-2">
              Hi <span className="font-semibold">{info.name}</span> — set a password to activate your <span className="font-semibold">{info.role.replace("_", " ")}</span> account
              at {info.email}.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="invite-form">
              <div className="space-y-1.5">
                <Label htmlFor="pw" className="label-caps">Choose a password</Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  minLength={6}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  data-testid="invite-password"
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm" className="label-caps">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="invite-confirm"
                  placeholder="Repeat password"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="invite-submit"
                className="w-full h-11 bg-teal-700 hover:bg-teal-800 rounded-full"
              >
                {busy ? "Activating…" : "Activate my account"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
